import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallIntelligenceV2Service, serviceName } from '../service';
import { ListFollowupsIO, ServiceQueries } from '../types';
import {
  UseCallIntelligenceV2InfiniteQueryEndpointArgs,
  useCallIntelligenceV2InfiniteQuery,
} from './use-call-intelligence-v2-infinite-query';

type ListFollowupsPageParams = {
  // TODO: add type for ListFollowups page parameters here
};
/**
 * A hook that returns an infinite query for the `ListFollowups` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListFollowupsQuery = <E = unknown, D = ListFollowupsIO['output']>({
  options,
  request,
  ...args
}: UseCallIntelligenceV2InfiniteQueryEndpointArgs<'ListFollowups', E, D, ListFollowupsPageParams>) =>
  useCallIntelligenceV2InfiniteQuery<'ListFollowups', E, D, ListFollowupsPageParams>({
    endpointName: 'ListFollowups',
    ...args,
    request: () => {
      const requestWithPageParams: ListFollowupsIO['input'] = {
        ...request,
      };
      return requestWithPageParams;
    },
    options: {
      ...options,
      getNextPageParam: (_, allPages) => {
        // TODO: calculate nextPageParam and return it here
        return allPages.length;
      },
    },
  });

/**
 * A hook that returns a function to fetch the query for the `ListFollowups` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListFollowups = () => {
  const queryClient = useQueryClient();

  const fetchListFollowups = async <E = unknown, D = ListFollowupsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallIntelligenceV2InfiniteQueryEndpointArgs<'ListFollowups', E, D>, 'options'> & {
    options: LimitedSchemaFetchQueryOptions<
      ListFollowupsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListFollowups'>>
    >;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        ListFollowupsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListFollowups'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListFollowups'>({
          serviceName,
          endpointName: 'ListFollowups',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligenceV2Service.ListFollowups(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError ?? ({} as D);
      }
      throw error;
    }
  };

  return fetchListFollowups;
};
